import type { AvatarConfig, AvatarKey, AvatarOption, HumanBase, ResolvedAvatarConfig } from './types';

export const AVATAR_KEYS: readonly AvatarKey[] = ['base', 'color', 'eyes', 'mouth', 'cheeks', 'hat', 'accessory', 'holding', 'background', 'skin', 'hair', 'hairColor'];

/** Hair styles the editor offers per human base; the first one is that base's default. */
export const HAIR_BY_BASE: Readonly<Record<HumanBase, readonly string[]>> = {
  boy: ['short', 'side-part', 'spiky', 'curly', 'buzz', 'mid-part'],
  girl: ['long', 'ponytail', 'pigtails', 'bun', 'bob', 'wavy'],
};

export const isHumanBase = (base: unknown): base is HumanBase => base === 'boy' || base === 'girl';

/** Default hair for a base (animal bases get the boy default; they never draw it). */
export const defaultHairFor = (base: string): string => (isHumanBase(base) ? HAIR_BY_BASE[base][0] : HAIR_BY_BASE.boy[0]);

export const AVATAR_OPTIONS: Readonly<Record<AvatarKey, readonly AvatarOption[]>> = {
  base: [
    { id: 'ghost', label: 'ผีน้อย' },
    { id: 'pumpkin', label: 'ฟักทอง' },
    { id: 'cat', label: 'แมวเหมียว' },
    { id: 'coffee-cup', label: 'แก้วกาแฟ' },
    { id: 'bear', label: 'หมีน้อย' },
    { id: 'boy', label: 'ผู้ชาย' },
    { id: 'girl', label: 'ผู้หญิง' },
  ],
  color: [
    { id: 'cream', label: 'ครีมนม' },
    { id: 'pumpkin', label: 'ส้มฟักทอง' },
    { id: 'caramel', label: 'คาราเมล' },
    { id: 'mocha', label: 'มอคค่า' },
    { id: 'matcha', label: 'มัทฉะ' },
    { id: 'berry', label: 'สตรอว์เบอร์รี' },
    { id: 'taro', label: 'เผือกหอม' },
    { id: 'blue', label: 'ฟ้าอัญชัน' },
  ],
  eyes: [
    { id: 'dot', label: 'ตากลม' },
    { id: 'sparkle', label: 'ตาวิบวับ' },
    { id: 'happy', label: 'ตายิ้ม' },
    { id: 'sleepy', label: 'ตาง่วง' },
    { id: 'wink', label: 'ขยิบตา' },
    { id: 'heart', label: 'ตาหัวใจ' },
  ],
  mouth: [
    { id: 'smile', label: 'ยิ้มหวาน' },
    { id: 'open', label: 'หัวเราะ' },
    { id: 'cat', label: 'ปากแมว' },
    { id: 'o', label: 'ปากโอ' },
    { id: 'tongue', label: 'แลบลิ้น' },
    { id: 'fang', label: 'เขี้ยวจิ๋ว' },
  ],
  cheeks: [
    { id: 'none', label: 'ไม่มี' },
    { id: 'pink', label: 'แก้มชมพู' },
    { id: 'orange', label: 'แก้มส้ม' },
  ],
  hat: [
    { id: 'none', label: 'ไม่ใส่' },
    { id: 'witch', label: 'หมวกแม่มด' },
    { id: 'beanie', label: 'หมวกไหมพรม' },
    { id: 'barista', label: 'หมวกบาริสต้า' },
    { id: 'bow', label: 'โบว์' },
    { id: 'headphones', label: 'หูฟัง' },
  ],
  accessory: [
    { id: 'none', label: 'ไม่มี' },
    { id: 'glasses', label: 'แว่นตา' },
    { id: 'scarf', label: 'ผ้าพันคอ' },
    { id: 'apron', label: 'ผ้ากันเปื้อน' },
  ],
  holding: [
    { id: 'none', label: 'มือว่าง' },
    { id: 'coffee', label: 'กาแฟ' },
    { id: 'tea', label: 'ชา' },
    { id: 'cake', label: 'เค้ก' },
    { id: 'cookie', label: 'คุกกี้' },
  ],
  background: [
    { id: 'cream-circle', label: 'วงกลมครีม' },
    { id: 'pumpkin-blob', label: 'ก้อนส้มนุ่ม' },
    { id: 'mint-square', label: 'สี่เหลี่ยมมิ้นต์' },
    { id: 'taro-flower', label: 'ดอกไม้ม่วง' },
    { id: 'sky-arch', label: 'ซุ้มฟ้า' },
  ],
  skin: [
    { id: 'skin-1', label: 'ผิวขาวอมชมพู' },
    { id: 'skin-2', label: 'ผิวขาวเหลือง' },
    { id: 'skin-3', label: 'ผิวสองสี' },
    { id: 'skin-4', label: 'ผิวแทน' },
    { id: 'skin-5', label: 'ผิวเข้ม' },
  ],
  hair: [
    { id: 'short', label: 'ผมสั้น' },
    { id: 'side-part', label: 'แสกข้าง' },
    { id: 'spiky', label: 'ผมตั้ง' },
    { id: 'curly', label: 'ผมหยิก' },
    { id: 'buzz', label: 'รองทรงสั้น' },
    { id: 'mid-part', label: 'แสกกลาง' },
    { id: 'long', label: 'ผมยาว' },
    { id: 'ponytail', label: 'หางม้า' },
    { id: 'pigtails', label: 'ผมแกละ' },
    { id: 'bun', label: 'มวยผม' },
    { id: 'bob', label: 'ผมบ็อบ' },
    { id: 'wavy', label: 'ผมลอน' },
  ],
  hairColor: [
    { id: 'black', label: 'ดำ' },
    { id: 'dark-brown', label: 'น้ำตาลเข้ม' },
    { id: 'brown', label: 'น้ำตาล' },
    { id: 'caramel', label: 'คาราเมล' },
    { id: 'blonde', label: 'บลอนด์' },
    { id: 'pink', label: 'ชมพู' },
    { id: 'ash', label: 'เทาแอช' },
  ],
};

export const DEFAULT_AVATAR: ResolvedAvatarConfig = {
  base: 'ghost',
  color: 'cream',
  eyes: 'sparkle',
  mouth: 'smile',
  cheeks: 'pink',
  hat: 'none',
  accessory: 'none',
  holding: 'coffee',
  background: 'cream-circle',
  skin: 'skin-2',
  hair: 'short',
  hairColor: 'dark-brown',
};

const isKnown = (key: AvatarKey, value: unknown): value is string =>
  typeof value === 'string' && AVATAR_OPTIONS[key].some(o => o.id === value);

/** Resolve every key to a known id; unknown, missing or malformed values fall back to DEFAULT_AVATAR. */
export function normalizeAvatar(config: Partial<AvatarConfig> | null | undefined): ResolvedAvatarConfig {
  const out = { ...DEFAULT_AVATAR };
  if (!config || typeof config !== 'object') return out;
  for (const key of AVATAR_KEYS) {
    const value = (config as Record<string, unknown>)[key];
    if (isKnown(key, value)) out[key] = value;
  }
  if (!isKnown('hair', (config as Record<string, unknown>).hair)) out.hair = defaultHairFor(out.base);
  return out;
}

/** Pick one option per key. Inject `rand` (returns [0,1)) for deterministic results. */
export function randomAvatar(rand: () => number = Math.random): ResolvedAvatarConfig {
  const out = { ...DEFAULT_AVATAR };
  const pick = <T,>(list: readonly T[]): T => {
    const r = Number(rand());
    return list[Number.isFinite(r) ? Math.min(list.length - 1, Math.max(0, Math.floor(r * list.length))) : 0];
  };
  for (const key of AVATAR_KEYS) {
    if (key === 'hair') continue;
    out[key] = pick(AVATAR_OPTIONS[key]).id;
  }
  out.hair = isHumanBase(out.base) ? pick(HAIR_BY_BASE[out.base]) : pick(AVATAR_OPTIONS.hair).id;
  return out;
}

export function avatarLabel(key: AvatarKey, id: string): string {
  return AVATAR_OPTIONS[key].find(o => o.id === id)?.label ?? id;
}
